import {
  mapURLSearchParamsToSorting,
  Sorting,
  SortingParam,
} from 'src/common/components/Filters/constants/sorting';
import { atom, SetStateAction } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { atomWithDefault, atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';

import { RSIM_INITIAL_FILTERS, RSimFilters } from 'src/rSimInventory/constants/rsim';
import { ACTIVE_PROFILE_FILTER } from 'src/rSimInventory/constants/activeProfile';
import { LAST_UPDATE_TIME_FILTERS } from 'src/rSimInventory/constants/lastUpdate';
import { SIMULATE_OUTAGE_FILTER } from 'src/rSimInventory/constants/simulateOutage';
import { createPaginationAtoms } from './utils/filtersFactory';
import { isArray, isEqual } from 'lodash';
import { ObjectValues } from 'src/common/tsHelpers';
import { mapURLSearchParamsToRsimFilters } from 'src/rSimInventory/Filters/SearchParams/constants/rsimSearchParams';
import { Routes } from 'src/Routes.constants';
import { cleanDefaultValuesURLSearchParams } from 'src/common/utils/urlSearchParamsUtils';
import { createSortingParamsSetter } from './utils/sorting';

const initialRSimFiltersAtom = atomWithDefault<RSimFilters>(() => {
  if (globalThis.location.pathname === Routes.productsRsim) {
    const params = new URLSearchParams(globalThis.location.search);
    return mapURLSearchParamsToRsimFilters(params);
  }

  return RSIM_INITIAL_FILTERS;
});

export const { rowsPerPageFilterAtom, pageFilterAtom } = createPaginationAtoms();

export const rsimFiltersAtom = atom(
  (get) => get(initialRSimFiltersAtom),
  (get, set, newValue: SetStateAction<RSimFilters> | typeof RESET) => {
    const currentRSimFilters = get(initialRSimFiltersAtom);
    if (!isEqual(currentRSimFilters, newValue)) {
      set(pageFilterAtom, 0);
    }

    if (newValue === RESET) {
      return set(initialRSimFiltersAtom, RSIM_INITIAL_FILTERS);
    }

    return set(initialRSimFiltersAtom, newValue);
  },
);

export const chosenIdTypeFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('chosenIdType'),
);

export const searchTextFilterAtom = focusAtom(rsimFiltersAtom, (optic) => optic.prop('searchText'));

export const lastActiveProfileFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('lastActiveProfile'),
);

export const lastUpdateTimeFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('lastUpdateTime'),
);

export const simulateOutageFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('simulateOutage'),
);

export const eidRangeFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.pick(['eidFrom', 'eidTo']),
);

export const connectionIdFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('connectionId'),
);

export const orderNumberFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('orderNumber'),
);

export const accountsFilterAtom = focusAtom(rsimFiltersAtom, (optic) => optic.prop('accounts'));

export const productCodesFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('productCodes'),
);

export const tagsFilterAtom = focusAtom(rsimFiltersAtom, (optic) => optic.prop('tags'));

export const rsimSubscriptionStatusFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('subscriptionStatus'),
);

export const rsimMobileNetworkOperatorFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('mobileNetworkOperator'),
);

export const rsimMobileNetworkOperatorIdFilterAtom = focusAtom(rsimFiltersAtom, (optic) =>
  optic.prop('mobileNetworkOperatorId'),
);

export const rsimMnoNamesFilterAtom = focusAtom(rsimFiltersAtom, (optic) => optic.prop('mnoNames'));

const atomLocalStorage = createJSONStorage<SortingParam>(() => localStorage);

const STORAGE_VERSION = 'v1';
const STORAGE_NAME = `rsim-products-order`;
const STORAGE_KEY = `${STORAGE_VERSION}-${STORAGE_NAME}`;

export const rsimSortingAtomWithStorage = atomWithStorage<SortingParam>(
  STORAGE_KEY,
  {
    columnName: 'EID',
    sort: 'asc',
  } as SortingParam,
  atomLocalStorage,
  { getOnInit: true },
);

export const hasRSimProductActiveFilterAtom = atom((get) => {
  const filters = get(rsimFiltersAtom);

  const entries = Object.entries(filters);

  return entries.some(([entryKey, entryValue]) => {
    const key = entryKey as keyof RSimFilters;
    const value = entryValue as ObjectValues<RSimFilters>;

    if (key === 'chosenIdType' || key === 'searchText') {
      return false;
    }

    if (
      (key === 'lastUpdateTime' && value === LAST_UPDATE_TIME_FILTERS.NONE) ||
      (key === 'lastActiveProfile' && value === ACTIVE_PROFILE_FILTER.ANY) ||
      (key === 'simulateOutage' && value === SIMULATE_OUTAGE_FILTER.ANY)
    ) {
      return false;
    }

    if (isArray(value) && isEqual(value, [])) {
      return false;
    }

    return value !== RSIM_INITIAL_FILTERS[key];
  });
});

const ALLOWED_SORTING = new Set(['EID', 'installerRef']);
const ALLOWED_SORTING_DIRECTION = new Set<Sorting>(['asc', 'desc']);

const setRSimSortingParams = createSortingParamsSetter({
  allowedColumnNames: ALLOWED_SORTING,
  allowedDirection: ALLOWED_SORTING_DIRECTION,
});

export const rsimProductsUrlParamsAtom = atom(
  (get) => {
    const cleanedFiltersValues = cleanDefaultValuesURLSearchParams(
      get(rsimFiltersAtom),
      RSIM_INITIAL_FILTERS,
    );

    const searchParams = new URLSearchParams({
      ...cleanedFiltersValues,
    });

    const sorting = get(rsimSortingAtomWithStorage);
    setRSimSortingParams({ searchParams, sorting });

    return searchParams;
  },
  (_, set, urlSearchParams: URLSearchParams) => {
    const filters = mapURLSearchParamsToRsimFilters(urlSearchParams);

    set(rsimFiltersAtom, filters);

    const sort = mapURLSearchParamsToSorting(urlSearchParams);

    if (sort) {
      set(rsimSortingAtomWithStorage, sort);
    }
  },
);
